import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useUser } from '../../auth/hooks/useUser';
import useBatchRun from '../hooks/useBatchRun';
import useBatchRunHistory from '../hooks/useBatchRunHistory';
import { useBatchRunStore } from '../stores/batchRunStore';
import { useRunStore } from '../../../store/automation/runStore';
import BatchRunTracker from './BatchRunTracker';
import GroupingService from '../../../shared/middleware/services/GroupingService';
import BatchService from '../../../shared/middleware/services/BatchService';
import { saveBatchSession, loadBatchSession, clearBatchSession } from '../utils/batchSession';
import { findReportFiles } from '../../../shared/middleware/services/ReportFileService';
import { groupingRunPrefix, groupingReportFolder } from '../../../shared/middleware/config/groupingPaths';
import logger from '../../../shared/services/logging/logger';

const BatchRunContext = createContext(null);

/** Run-history statuses that mean a batch is over. */
const ENDED_BATCH_STATUSES = new Set(['PASSED', 'FAILED', 'PARTIALLY_PASSED', 'CANCELLED', 'COMPLETED']);

/** The member fields a restore needs — exactly what `startBatch` seeds from. */
const toSavedMember = (m) => ({
  runId: m.runId,
  runnableId: m.runnableId,
  name: m.name,
  scriptTypes: m.scriptTypes,
  status: m.status,
  failureReason: m.failureReason,
  logPath: m.logPath,
  reportPath: m.reportPath,
  summaryPath: m.summaryPath,
});

const HTML_ONLY = ['html'];
const HTML_AND_PDF = ['html', 'pdf'];

/** How long to keep looking for a PDF rendered after the HTML: 12 x 5s = 1 minute. */
const PDF_LOOK_ATTEMPTS = 12;
const PDF_LOOK_INTERVAL_MS = 5000;

/**
 * Owns the batch run for the whole app, the way the single run is owned above
 * every page (`GlobalRunProgress`). The run window, its stream, its report and
 * the grouping stamps all live here, so leaving the group page does not end
 * them — the bottom bar stays on every page until the user closes it.
 *
 * The group page starts a run through `startRun`; everything after that is
 * handled here.
 */
export const BatchRunProvider = ({ children }) => {
  const { userData } = useUser();
  const [trackerOpen, setTrackerOpen] = useState(false);
  // Bumped per run so the run window mounts fresh — none of the previous run's
  // notes, errors or frozen bars carry into the next one
  const [runKey, setRunKey] = useState(0);

  // Which grouping file the running batch belongs to — needed to stamp it
  const runRef = useRef(null); // { fileKey, projectId, projectName, groupingName }

  /**
   * Stamp the terminal outcome onto the grouping when the batch finishes —
   * wherever the user happens to be at the time.
   */
  const handleBatchComplete = useCallback(async ({ status, passRate }) => {
    // Over — a refresh must not bring it back
    clearBatchSession();
    const run = runRef.current;
    if (!run?.fileKey || !run.projectName) return;
    try {
      await GroupingService.stampRun(
        run.fileKey, run.projectId, run.projectName, { status, lastPassRate: passRate }, userData
      );
    } catch (err) {
      logger.warn('Could not stamp the grouping with its run outcome', { error: err.message, fileKey: run.fileKey });
    }
  }, [userData]);

  const batch = useBatchRun(userData, { onBatchComplete: handleBatchComplete });
  // Only its download is used — the same stored-report download History uses
  const reportDownloads = useBatchRunHistory();

  /**
   * Start a grouping's batch. Every run starts from nothing: the previous
   * run's window, stream, report and tracker state are cleared first.
   *
   * @throws when the grouping cannot be submitted — the caller reports it
   */
  const startRun = useCallback(async ({
    projectId, projectName, fileKey, groupingId, groupingName, runnableGroupPath,
  }) => {
    // Single and batch runs share one stream socket, so only one run window may
    // be live. Close a single run's window too — teardown drops its socket, and
    // leaving the window up would show a frozen run beside the new batch.
    const single = useRunStore.getState();
    if (single.activeRunMeta || single.runState) single.dismissRun();
    batch.teardown();
    setTrackerOpen(false);

    // The Middleware owns path naming; a missing path is a server bug to surface
    if (!runnableGroupPath) {
      throw new Error(
        'This grouping came back without a runnableGroupPath. That is a server-side ' +
        'problem — the batch cannot be submitted until the load endpoint returns it.'
      );
    }

    runRef.current = { fileKey, projectId, projectName, groupingName };
    const submitted = await batch.submit({ projectId, projectName, runnableGroupPath, groupingName, groupingId });

    // Kept so a refresh mid-run can bring the window back. Cleared the moment
    // the batch is over, closed, or replaced.
    const seeded = useBatchRunStore.getState();
    saveBatchSession({
      batchId: submitted.batchId,
      batchRunNumber: submitted.batchRunNumber,
      groupingId,
      fileKey,
      groupingName,
      projectId,
      projectName,
      timeZone: submitted.timeZone,
      webSocketUrl: submitted.webSocketUrl,
      members: seeded.order.map((runId) => seeded.members[runId]).filter(Boolean).map(toSavedMember),
      startedAt: seeded.startedAt,
    });

    // Stamp at submit, not only at completion — otherwise a user who closes the
    // tab mid-run leaves the grouping showing the previous run forever
    try {
      await GroupingService.stampRun(
        fileKey, projectId, projectName,
        { status: 'RUNNING', lastRunAt: new Date().toISOString(), lastBatchId: submitted.batchId },
        userData
      );
    } catch (err) {
      // The batch is away; a failed stamp is cosmetic, not a failed run
      logger.warn('Batch started but the grouping could not be stamped', { error: err.message });
    }

    setRunKey((k) => k + 1);
    setTrackerOpen(true);
    return submitted;
  }, [batch, userData]);

  const closeRun = useCallback(() => {
    clearBatchSession();
    batch.teardown();
    setTrackerOpen(false);
    runRef.current = null;
  }, [batch]);

  // A single run started anywhere replaces this batch's window. Its connect()
  // takes over the shared socket, so a batch window left open would freeze
  // mid-run and read as live. Read through a ref — `closeRun` changes with
  // `batch` on every render and the subscription should not churn with it.
  const closeRunRef = useRef(closeRun);
  closeRunRef.current = closeRun;
  useEffect(() => useRunStore.subscribe((state, prev) => {
    if (state.runNonce === prev.runNonce) return;
    if (!useBatchRunStore.getState().batchId) return;
    logger.info('Single run started — closing the batch run window', {
      batchId: useBatchRunStore.getState().batchId,
    });
    closeRunRef.current();
  }), []);

  // The batch's own verdict frame means it is over
  useEffect(() => useBatchRunStore.subscribe((state) => {
    if (state.batchStatus) clearBatchSession();
  }), []);

  /**
   * Bring a batch back after a page refresh — but only once the server has
   * confirmed it is still running. The saved slot is a hint, never the truth:
   *
   *   1. The run history decides first. A batch it lists as ended is dropped.
   *   2. Each script is re-read fresh (uncached, statuses translated). If every
   *      one has finished, the batch is dropped — its result is in History.
   *   3. Only a batch still running reopens the window and its socket.
   *
   * If a check cannot be made, nothing reopens and the slot is kept, so the
   * next load tries again. A stale window is never shown on a guess.
   */
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current || !userData) return;
    resumedRef.current = true;
    const session = loadBatchSession();
    if (!session) return;

    (async () => {
      try {
        const { items } = await BatchService.listBatches(session.projectId, session.groupingId, {}, userData);
        const entry = (items || []).find((b) => b.batchId === session.batchId);
        if (entry && (entry.endedAt || ENDED_BATCH_STATUSES.has(String(entry.status).toUpperCase()))) {
          logger.info('Saved batch has already finished — not reopening it', { batchId: session.batchId });
          clearBatchSession();
          return;
        }
      } catch (err) {
        logger.warn('Could not check the saved batch against run history — not reopening it', { error: err.message });
        return;
      }

      runRef.current = {
        fileKey: session.fileKey,
        projectId: session.projectId,
        projectName: session.projectName,
        groupingName: session.groupingName,
      };
      try {
        await batch.rehydrate(session);
      } catch (err) {
        logger.warn('Could not rebuild the saved batch — not reopening it', { error: err.message });
        batch.teardown();
        return;
      }

      const state = useBatchRunStore.getState();
      const dispatched = state.order.filter((runId) => state.members[runId]?.runId);
      const allFinished = dispatched.length > 0 && dispatched.every((runId) => state.terminal[runId]);
      if (allFinished) {
        logger.info('Saved batch finished while the page was away — not reopening it', { batchId: session.batchId });
        clearBatchSession();
        batch.teardown();
        return;
      }

      logger.info('Resuming batch run after page load', { batchId: session.batchId });
      setRunKey((k) => k + 1);
      setTrackerOpen(true);
    })();
    // `batch` is a fresh object each render; this runs once per page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  /** The finished run's stored report, as HTML or PDF. */
  const downloadReport = useCallback((format = 'html') => {
    const { projectId, projectName, groupingId, batchRunNumber } = useBatchRunStore.getState();
    const reportFolder = groupingRunPrefix(projectId, projectName, groupingId, batchRunNumber);
    if (!reportFolder) return;
    reportDownloads.downloadReport({
      projectId,
      groupingName: runRef.current?.groupingName,
      run: { reportFolder, runNumber: batchRunNumber },
      userData,
      format,
    });
  }, [reportDownloads, userData]);

  /**
   * Whether the finished run also has a PDF — drives the HTML / PDF dropdown.
   *
   * History screens are opened long after a run, when the PDF is already there.
   * This one is asked the moment the batch ends, and the PDF is rendered after
   * the HTML — a single look found only the HTML and the choice never appeared.
   * So keep looking for a while; stop as soon as the PDF turns up.
   */
  const [reportFormats, setReportFormats] = useState(HTML_ONLY);
  useEffect(() => {
    // Same array every time — a fresh ['html'] would re-render on every pass
    if (!batch.report) { setReportFormats(HTML_ONLY); return undefined; }
    const { projectId, projectName, groupingId, batchRunNumber } = useBatchRunStore.getState();
    const folder = groupingReportFolder(projectId, projectName, groupingId, batchRunNumber);
    if (!folder) return undefined;

    let cancelled = false;
    let timer = null;
    let attempts = 0;
    const look = () => {
      attempts += 1;
      findReportFiles(projectId, folder, userData)
        .catch(() => ({ pdf: null }))
        .then(({ pdf }) => {
          if (cancelled) return;
          if (pdf) { setReportFormats(HTML_AND_PDF); return; }
          if (attempts < PDF_LOOK_ATTEMPTS) timer = setTimeout(look, PDF_LOOK_INTERVAL_MS);
        });
    };
    look();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [batch.report, userData]);

  const value = useMemo(() => ({
    trackerOpen,
    runKey,
    startRun,
    closeRun,
    batch,
    downloadReport,
    downloadingReport: reportDownloads.downloading,
    reportFormats,
  }), [trackerOpen, runKey, startRun, closeRun, batch, downloadReport, reportDownloads.downloading, reportFormats]);

  return <BatchRunContext.Provider value={value}>{children}</BatchRunContext.Provider>;
};

BatchRunProvider.propTypes = {
  children: PropTypes.node,
};

BatchRunProvider.defaultProps = {
  children: null,
};

/** The app-wide batch run: `startRun`, `closeRun`, `trackerOpen`. */
export const useBatchRunControl = () => {
  const ctx = useContext(BatchRunContext);
  if (!ctx) throw new Error('useBatchRunControl must be used inside BatchRunProvider');
  return ctx;
};

/**
 * The batch run window, docked to the bottom of every page — the batch
 * counterpart of `GlobalRunProgress`.
 */
export const GlobalBatchRunProgress = () => {
  const {
    trackerOpen, runKey, closeRun, batch, downloadReport, downloadingReport, reportFormats,
  } = useBatchRunControl();
  if (!trackerOpen) return null;

  return (
    <BatchRunTracker
      key={runKey}
      onCancelBatch={batch.cancelBatch}
      onClose={closeRun}
      report={batch.report}
      reportLoading={batch.reportLoading}
      reportError={batch.reportError}
      onRetryReport={() => batch.fetchReport({ retry: true })}
      onDownloadReport={downloadReport}
      reportFormats={reportFormats}
      downloadingReport={downloadingReport}
    />
  );
};
